import React, { useState, useEffect } from 'react';
import { X, Paperclip, Clock } from 'lucide-react';
import { Email, EmailTemplate } from '../../../types/email';

interface EmailComposeProps {
  onClose: () => void;
  onSend?: (email: Omit<Email, 'id' | 'timestamp' | 'folder'>) => void;
  replyTo?: Email | null;
  forward?: Email | null;
  template?: EmailTemplate | null;
}

export default function EmailCompose({
  onClose,
  onSend,
  replyTo,
  forward,
  template
}: EmailComposeProps) {
  const [formData, setFormData] = useState({
    to: '',
    subject: '',
    content: ''
  });

  useEffect(() => {
    if (replyTo) {
      setFormData({
        to: replyTo.from,
        subject: `Re: ${replyTo.subject}`,
        content: `\n\nOn ${new Date(replyTo.timestamp).toLocaleString()}, ${replyTo.from} wrote:\n> ${replyTo.content.split('\n').join('\n> ')}`
      });
    } else if (forward) {
      setFormData({
        to: '',
        subject: `Fwd: ${forward.subject}`,
        content: `\n\n---------- Forwarded message ----------\nFrom: ${forward.from}\nDate: ${new Date(forward.timestamp).toLocaleString()}\nSubject: ${forward.subject}\n\n${forward.content}`
      });
    } else if (template) {
      setFormData({
        to: '',
        subject: template.subject,
        content: template.content
      });
    }
  }, [replyTo, forward, template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSend) {
      onSend({
        from: 'user@company.com',
        to: formData.to.split(',').map(email => email.trim()),
        subject: formData.subject,
        content: formData.content,
        isRead: true,
        isStarred: false,
        hasAttachments: false
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {replyTo ? 'Reply' : forward ? 'Forward' : 'New Message'}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <input
              type="text"
              placeholder="To"
              value={formData.to}
              onChange={(e) => setFormData({ ...formData, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <input
              type="text"
              placeholder="Subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <textarea
              rows={12}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="flex justify-between">
            <div className="flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Send
              </button>
              <button
                type="button"
                className="px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <button
                type="button"
                className="px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                <Clock className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              Discard
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}